import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule } from '@jsverse/transloco';
import { LinksService } from 'libs/shared/domain/src/lib/infrastructure/links.service';
import { ScrollService } from 'libs/shared/domain/src/lib/infrastructure/scroll.service';
import { IconDirective } from 'libs/shared/ui-common/src/lib/components/icon/icon.directive';
import { TooltipDirective } from 'libs/shared/ui-common/src/lib/components/tooltip/tooltip.directive';
import { WrapFnPipe } from 'libs/shared/util-common/src/lib/pipes/wrapfn.pipe';

import { LinkElementType, GuidelineLinkDto } from '@treetop/shared/model';

type IconType = 'book' | 'bookmark' | 'component' | 'git-fork' | 'new-tab';

type LinkItem = GuidelineLinkDto & {
  icon: IconType;
  translationKey: string;
  openInNewTab?: boolean;
};

@Component({
  selector: 'tt-node-detail-popover-links',
  standalone: true,
  imports: [TranslocoModule, MatIconModule, IconDirective, MatTooltipModule, TooltipDirective, WrapFnPipe],
  templateUrl: './node-detail-popover-links.component.html',
  styleUrl: './node-detail-popover-links.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NodeDetailPopoverLinksComponent {
  private scrollService = inject(ScrollService);
  private linksService = inject(LinksService);

  @Input() public isPublicPage = false;
  @Input() public isPreviewMode = false;
  @Input() public isUpdateReviewMode = false;

  @Input({
    transform: (value: GuidelineLinkDto[]): LinkItem[] =>
      value.map((link) => ({
        ...link,
        ...NodeDetailPopoverLinksComponent.getLinkData(link),
      })),
  })
  public links: LinkItem[];

  private static getLinkData(link: GuidelineLinkDto): { icon: IconType; translationKey: string } {
    let icon: IconType;
    let translationKey: string;

    const prefix = 'Flowchart.NodeDetailDialog.Links.';

    if ((link.toUrl && link.toElementType === LinkElementType.External) || link.toElementType === LinkElementType.Compendium) {
      icon = 'new-tab';
      translationKey = 'GoToLink';
    } else if (link.toElementType === LinkElementType.Widget) {
      icon = 'bookmark';
      translationKey = 'GoToElement';
    } else if (link.toElementType === LinkElementType.FlowchartNode) {
      icon = 'git-fork';
      translationKey = 'GoToFlowchart';
    } else if (link.toElementType === LinkElementType.Guideline) {
      icon = 'book';
      translationKey = 'GoToGuideline';
    }

    return { icon, translationKey: `${prefix}${translationKey}` };
  }

  protected handleLinkClick(link: LinkItem) {
    if (link.toUrl && (link.toElementType === LinkElementType.External || link.toElementType === LinkElementType.Compendium)) {
      window.open(link.toUrl, '_blank');
      return;
    }

    if (link.toUrl && !(link.toElementType == LinkElementType.External || link.toElementType === LinkElementType.Compendium)) {
      this.linksService.openExternalGuideline(link.toUrl, this.isPublicPage, this.isPreviewMode, this.isUpdateReviewMode);

      return;
    }

    if (!link.toUrl && link.toElementType === LinkElementType.Widget) {
      this.scrollService.scrollTo(link.toElementUniqueId, true, true);

      return;
    }

    if (!link.toUrl && link.toElementType === LinkElementType.FlowchartNode) {
      this.scrollService.scrollToFlowchartElement(link.toElementUniqueId);

      return;
    }
  }

  protected getTextContent(input = ''): string {
    const doc = new DOMParser().parseFromString(input, 'text/html');
    return doc.body.textContent || '';
  }
}
